import { UserOnEntity } from "@/graphql/__generated__/graphql";
import { UseFormReturn } from "react-hook-form";
import { Dispatch, SetStateAction } from "react";

/**
 * Replaces a partial mention in the chat input with the full username.
 *
 * When the user selects a mention from the dropdown, this function finds the partial
 * mention (e.g., "@use") in the current input text, replaces it with the full name (e.g., "@John Doe "),
 * and resets the mention query.
 *
 * @param user - An object containing the user's id, firstname, and lastname.
 */
export const selectUserFromMention = (
  user: { id: number; firstname: string; lastname: string },
  inputRef: React.RefObject<HTMLInputElement | HTMLTextAreaElement>,
  form: UseFormReturn<any>,
  formField: string,
  setMentionQuery: (_mentionQuery: string | null) => void
): void => {
  // Get reference to input element
  const element = inputRef.current;
  if (!element) return;

  // Get current input value and caret position
  const currentValue = form.getValues(formField);
  const caretPos = element.selectionStart ?? 0;
  // Normalize text in case special characters are in decomposed form
  const textBeforeCaret = currentValue.slice(0, caretPos).normalize("NFC");

  // Updated regex to include German special characters
  const match = textBeforeCaret.match(/@([A-Za-z0-9_äöüÄÖÜß]*)$/);
  if (match) {
    // Calculate positions for text replacement
    const startIndex = caretPos - match[0].length;
    const insertedText = `@${user.firstname} ${user.lastname} `;
    const newText = currentValue.slice(0, startIndex) + insertedText + currentValue.slice(caretPos);

    // Update form value and reset mention query
    form.setValue(formField, newText);
    setMentionQuery(null);

    // Set caret position after inserted mention
    const newCaretPos = startIndex + insertedText.length;
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.setSelectionRange(newCaretPos, newCaretPos);
      }
    }, 0);
  }
};

// Helper to escape RegExp special characters
export function escapeRegExp(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Transforms display mentions in a message to token mentions for submission.
 *
 * This function replaces all occurrences of display mentions (e.g., "@John Doe")
 * with corresponding token mentions (e.g., "@User:1") based on the list of tenderUsers.
 *
 * @param {string} message - The chat message containing display mentions
 * @param {UserOnEntity[]} tenderUsers - Array of users to match against display mentions
 * @returns {string} The transformed message with token mentions
 * @example
 * const message = "Hello @John Doe, how are you?";
 * const users = [{id: 1, firstname: "John", lastname: "Doe"}];
 * transformMessageForSubmission(message, users);
 * // Returns: "Hello @User:1, how are you?"
 */
export const transformMessageForSubmission = (message: string, tenderUsers: UserOnEntity[]): string => {
  let transformed = message;
  tenderUsers.forEach(user => {
    const displayMention = "@" + user.firstname + " " + user.lastname;
    const tokenMention = "@User:" + user.id;
    const regex = new RegExp(escapeRegExp(displayMention), "g");
    transformed = transformed.replace(regex, tokenMention);
  });
  return transformed;
};

/**
 * Transforms token mentions in a message back to display mentions.
 *
 * This function replaces all occurrences of token mentions (e.g., "@User:1")
 * with corresponding display mentions (e.g., "@John Doe") based on the list of tenderUsers.
 *
 * @param {string} message - The message containing token mentions to transform
 * @param {UserOnEntity[]} tenderUsers - Array of users to match against token mentions
 * @returns {string} The transformed message with display mentions
 */
export const transformMessageForDisplay = (message: string, tenderUsers: UserOnEntity[]): string => {
  let transformed = message;
  tenderUsers.forEach(user => {
    const tokenMention = "@User:" + user.id;
    const displayMention = "@" + user.firstname + " " + user.lastname;
    const regex = new RegExp(escapeRegExp(tokenMention), "g");
    transformed = transformed.replace(regex, displayMention);
  });
  return transformed;
};

/**
 * Detects a mention query in an input event by looking for a pattern that starts with "@".
 *
 * This function checks the input value from the beginning of the text up to the current caret position.
 * If it finds a substring that matches the pattern "@word" (where "word" is made up of word characters),
 * it extracts the "word" part and uses the provided callback to update the mention query. If no valid mention
 * is found, it resets the mention query by passing null.
 *
 * @param {React.ChangeEvent<HTMLInputElement|HTMLTextAreaElement>} e - The change event from the input element.
 * @param {(mentionQuery: string | null) => void} setMentionQuery - Callback to update the current mention query.
 * @returns {void}
 */
export const handleMentionDetection = (
  e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  setMentionQuery: Dispatch<SetStateAction<string | null>>
): void => {
  const value = e.target.value;
  const caretPos = e.target.selectionStart || 0;
  // Normalize substring to ensure composed accented characters
  const substring = value.substring(0, caretPos).normalize("NFC");
  // Regex includes German special characters: ä, ö, ü, Ä, Ö, Ü, ß
  const match = substring.match(/@([A-Za-z0-9_äöüÄÖÜß]*)$/);

  if (match) {
    setMentionQuery(match[1]);
  } else {
    setMentionQuery(null);
  }
};

/**
 * Filters tender users based on a mention query.
 *
 * @param query - The mention query string.
 * @param tenderUsers - The array of user objects.
 * @returns The filtered array of user objects.
 */
export const filterUsersByMention = (
  query: string | null,
  tenderUsers: UserOnEntity[]
): UserOnEntity[] => {
  return tenderUsers.filter(
    (user) =>
      user.firstname.toLowerCase().includes((query || "").toLowerCase()) ||
      user.lastname.toLowerCase().includes((query || "").toLowerCase())
  );
};
